import React from "react";
import { motion } from "framer-motion";
import { Icon } from "./icon";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const Header = (props) => {
  const data = props.data;

  return (
    <header
      id="header"
      className="on-dark relative flex min-h-screen items-center overflow-hidden bg-navy-950 pt-28 pb-20"
    >
      {/* Photographic base, heavily dimmed so it reads as texture, not subject */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.22]"
        style={{ backgroundImage: "url(img/intro-bg.jpg)" }}
        aria-hidden="true"
      />
      {/* Brand gradient wash */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900/95 to-navy-800"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/70"
        aria-hidden="true"
      />
      {/* Blueprint grid + crimson glow */}
      <div className="grid-bg-dark mask-fade absolute inset-0 opacity-70" aria-hidden="true" />
      <div
        className="animate-float absolute -right-24 top-1/4 h-[34rem] w-[34rem] rounded-full bg-crimson-600/25 blur-[130px]"
        aria-hidden="true"
      />
      <div
        className="absolute -left-32 bottom-0 h-[30rem] w-[30rem] rounded-full bg-navy-500/30 blur-[130px]"
        aria-hidden="true"
      />

      <div className="container relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-3xl"
        >
          <motion.p variants={item}>
            <span className="eyebrow-dark">
              <span className="h-1.5 w-1.5 rounded-full bg-crimson-400" />
              {data ? data.eyebrow : "Software engineering studio"}
            </span>
          </motion.p>

          <motion.h1
            variants={item}
            className="mt-7 font-display text-display-sm font-extrabold text-white md:text-display-lg lg:text-display-xl"
          >
            {data ? (
              <>
                <span className="block">{data.title}</span>
                <span className="gradient-text-light block">{data.highlight}</span>
              </>
            ) : (
              <span className="block">Software that moves your business forward</span>
            )}
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-7 max-w-xl text-lg leading-relaxed text-white/70 md:text-xl"
          >
            {data ? data.paragraph : ""}
          </motion.p>

          <motion.div variants={item} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <a href={data ? data.primaryCta.href : "#contact"} className="btn-primary group">
              {data ? data.primaryCta.label : "Start a project"}
              <Icon
                name="arrowRight"
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a href={data ? data.secondaryCta.href : "#portfolio"} className="btn-ghost-light">
              {data ? data.secondaryCta.label : "See our work"}
            </a>
          </motion.div>

          <motion.ul
            variants={item}
            className="mt-14 flex flex-col gap-x-8 gap-y-3 border-t border-white/10 pt-8 text-sm text-white/60 sm:flex-row sm:flex-wrap"
          >
            {(data ? data.highlights : []).map((highlight) => (
              <li key={highlight} className="flex items-center gap-2.5">
                <Icon name="check" className="h-4 w-4 shrink-0 text-crimson-400" />
                {highlight}
              </li>
            ))}
          </motion.ul>
        </motion.div>
      </div>

      {/* Scroll affordance */}
      <motion.a
        href="#stats"
        aria-label="Scroll to content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/40 transition-colors hover:text-white lg:flex"
      >
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em]">Scroll</span>
        <span className="flex h-9 w-5 items-start justify-center rounded-full border border-current p-1">
          <motion.span
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-1.5 w-1 rounded-full bg-current"
          />
        </span>
      </motion.a>
    </header>
  );
};
